import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

// Parametri facilmente regolabili in base al riscontro reale con gli utenti
const INTERVALLO_MINUTI = 20         // ogni quanto si ripropone lo sponsor a schermo intero
const PRIMA_APERTURA_SECONDI = 45    // attesa dopo l'apertura dell'app prima del primo sponsor
const SECONDI_PRIMA_CHIUDIBILE = 5   // secondi minimi prima che l'utente possa chiudere

/**
 * Un allenamento non va mai interrotto: mentre l'atleta è dentro una sessione
 * (o ha il timer aperto) lo sponsor a schermo intero non compare. La finestra
 * saltata non si accumula: si riproverà al giro successivo, a sessione conclusa.
 * Il flag è impostato da HomeAtleta.js, che è dove vivono le sessioni.
 */
export function setAllenamentoInCorso(attivo) {
  window.__fpAllenamentoInCorso = !!attivo
}
function allenamentoInCorso() {
  return !!window.__fpAllenamentoInCorso
}

async function segnaVisualizzazione(id) {
  try { await supabase.rpc('sponsor_incrementa_visualizzazione', { sponsor_id: id }) } catch {}
}
async function segnaClick(id) {
  try { await supabase.rpc('sponsor_incrementa_click', { sponsor_id: id }) } catch {}
}
function normalizzaLink(link) {
  if (!link) return null
  return /^https?:\/\//i.test(link) ? link : `https://${link}`
}

export default function SponsorInterstitial() {
  const [sponsorCorrente, setSponsorCorrente] = useState(null)
  const [secondiRimanenti, setSecondiRimanenti] = useState(SECONDI_PRIMA_CHIUDIBILE)
  const listaRef = useRef([])
  const contatoreRef = useRef(0)

  // Carica gli sponsor attivi una volta sola
  useEffect(() => {
    const oggi = new Date().toISOString().split('T')[0]
    supabase.from('sponsor').select('*')
      .eq('attivo', true).lte('data_inizio', oggi).gte('data_fine', oggi)
      .order('ordine', { ascending: true })
      .then(({ data }) => { listaRef.current = data || [] })
  }, [])

  // Timer periodico di apertura
  useEffect(() => {
    const primoTimer = setTimeout(apri, PRIMA_APERTURA_SECONDI * 1000)
    const intervallo = setInterval(apri, INTERVALLO_MINUTI * 60 * 1000)
    return () => { clearTimeout(primoTimer); clearInterval(intervallo) }
  }, [])

  function apri() {
    const lista = listaRef.current
    if (lista.length === 0) return
    if (allenamentoInCorso()) return // mai in mezzo a una serie
    const s = lista[contatoreRef.current % lista.length]
    contatoreRef.current += 1
    setSponsorCorrente(s)
    setSecondiRimanenti(SECONDI_PRIMA_CHIUDIBILE)
    segnaVisualizzazione(s.id)
  }

  // Se l'atleta avvia un allenamento mentre lo sponsor è già a schermo,
  // lo sponsor si chiude da solo invece di restare sopra al timer.
  useEffect(() => {
    if (!sponsorCorrente) return
    const controllo = setInterval(() => {
      if (allenamentoInCorso()) setSponsorCorrente(null)
    }, 1000)
    return () => clearInterval(controllo)
  }, [sponsorCorrente])

  // Conto alla rovescia prima di poter chiudere
  useEffect(() => {
    if (!sponsorCorrente || secondiRimanenti <= 0) return
    const t = setTimeout(() => setSecondiRimanenti(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [sponsorCorrente, secondiRimanenti])

  if (!sponsorCorrente) return null

  function chiudi() {
    if (secondiRimanenti > 0) return
    setSponsorCorrente(null)
  }

  function apriLink() {
    if (!sponsorCorrente.link) return
    segnaClick(sponsorCorrente.id)
    window.open(normalizzaLink(sponsorCorrente.link), '_blank', 'noopener,noreferrer')
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(6px)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <button onClick={chiudi} disabled={secondiRimanenti > 0} aria-label="Chiudi" style={{
        position: 'absolute', top: 20, right: 20,
        width: 40, height: 40, borderRadius: '50%',
        background: secondiRimanenti > 0 ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.18)',
        border: 'none', color: secondiRimanenti > 0 ? 'var(--muted)' : '#fff',
        fontSize: secondiRimanenti > 0 ? 14 : 20, fontWeight: 700,
        cursor: secondiRimanenti > 0 ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.2s',
      }}>
        {secondiRimanenti > 0 ? secondiRimanenti : '✕'}
      </button>

      <div onClick={apriLink} style={{ cursor: sponsorCorrente.link ? 'pointer' : 'default', textAlign: 'center', maxWidth: 420, width: '100%' }} className="fade-in">
        <img src={sponsorCorrente.immagine_url} alt={sponsorCorrente.nome}
          style={{ maxWidth: '100%', maxHeight: '58vh', borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }} />
        <div style={{ marginTop: 16, fontFamily: 'Syne', fontWeight: 800, fontSize: 18, color: '#fff' }}>{sponsorCorrente.nome}</div>
        {sponsorCorrente.link && <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>Tocca per saperne di più →</div>}
      </div>

      <div style={{ position: 'absolute', bottom: 24, fontSize: 11, color: 'var(--muted)' }}>Contenuto sponsorizzato</div>
    </div>
  )
}
